import { Input, Modal } from '../../../components'
import React, { useEffect, useState } from 'react';
import Icon from '../../../components/icon/icon';
import { X } from '@phosphor-icons/react';

const RefundModal = ({
  onSave,      // Function to handle saving the data (API call, etc.)
  isOpen,      // Boolean to indicate if the modal is open
  onClose,     // Function to handle closing the modal
  payout,      // Pre-existing payout details if any
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({});

  // Helper function to convert string to Pascal case
  const toPascalCase = (str) => {
    return str
      .replace(/(^\w|[\s-]+\w)/g, match => match.toUpperCase())
      .replace(/[\s-]/g, ' ');
  };

  // Handle form field changes and apply formatting where necessary
  const handleChange = (e) => {
    const { name, value } = e.target;
    const valueUpdate = name === 'account_holder_name' ? toPascalCase(value) : value;
    setFormData({
      ...formData,
      [name]: name === 'ifsc_code' ? valueUpdate.toUpperCase() : valueUpdate,
    });
  };

  // Populate the form with existing payout data if available
  useEffect(() => {
    if (payout) {
      setFormData(payout);
      setIsEditMode(true);
    } else {
      setFormData({});
      setIsEditMode(false);
    }
  }, [payout]);

  // Form validation logic
  const validateForm = () => {
    const errors = {};
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

    if (!formData.account_holder_name) errors.account_holder_name = 'Account holder name is required.';
    if (!formData.account_number) errors.account_number = 'Account number is required.';
    if (!formData.ifsc_code) {
      errors.ifsc_code = 'IFSC code is required.';
    } else if (!ifscRegex.test(formData.ifsc_code)) {
      errors.ifsc_code = 'Invalid IFSC code format.';
    }
    if (!formData.bank_name) errors.bank_name = 'Bank name is required.';
    if (!formData.branch_name) errors.branch_name = 'Branch name is required.';

    setErrors(errors);
    return Object.keys(errors).length === 0;  // Return true if no errors
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!formData.id) {
      formData.id = '0';  // Assign default id if not present
    }

    if (validateForm()) {
      onSave(formData);  // Call the onSave function to handle form data submission
      onClose();  // Close the modal upon successful submission
    }
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        primaryText='Submit'
        secondaryText={null}
        onClickPrimaryCta={handleSubmit}  // Call handleSubmit when clicking the primary CTA button
        className={'payout-modal secondary-cta-hidden'}
        size="medium"
        title={
          <div className='flex justify-between w-full'>
            <div className='flex heading-24'>Add NEFT details</div>
            <Icon type={'circle'} onClick={onClose}>
              <X size={16} />
            </Icon>
          </div>
        }
        onClose={onClose}  // Handle modal close action
      >
        <div className='flex flex-col gap-4 md:gap-8'>
          <div className='label-14 md:label-16 text-grey-70'>We’ll refund you the money within 2-7 business days.</div>

          {/* Account Holder Name */}
          <Input
            name='account_holder_name'
            value={formData.account_holder_name || ''}
            label="Account Holder Name"
            onChange={handleChange}
            validationStatus={errors.account_holder_name ? 'error' : null}
            helpText={errors.account_holder_name}
            required
            width={window.innerWidth <650 ? '100%' : 420}
            maxLength={120}
          />

          {/* Account Number */}
          <Input
            name='account_number'
            value={formData.account_number || ''}
            label='Account Number'
            onChange={handleChange}
            validationStatus={errors.account_number ? 'error' : null}
            helpText={errors.account_number}
            required
            width={window.innerWidth <650 ? '100%' : 420}
            type={'number'}
            maxLength={20}
          />

          {/* IFSC Code */}
          <Input
            name='ifsc_code'
            value={formData.ifsc_code || ''}
            label='IFSC Code'
            onChange={handleChange}
            validationStatus={errors.ifsc_code ? 'error' : null}
            helpText={errors.ifsc_code}
            required
            width={window.innerWidth <650 ? '100%' : 420}
            maxLength={11}
          />

          {/* Bank Name */}
          <Input
            name='bank_name'
            value={formData.bank_name || ''}
            label='Bank Name'
            onChange={handleChange}
            validationStatus={errors.bank_name ? 'error' : null}
            helpText={errors.bank_name}
            required
            width={window.innerWidth <650 ? '100%' : 420}
            maxLength={300}
          />

          {/* Branch Name */}
          <Input
            name='branch_name'
            value={formData.branch_name || ''}
            label='Branch Name'
            onChange={handleChange}
            validationStatus={errors.branch_name ? 'error' : null}
            helpText={errors.branch_name}
            required
            width={window.innerWidth <650 ? '100%' : 420}
            maxLength={200}
          />
        </div>
      </Modal>
    </div>
  );
};

export default RefundModal;
